import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { downloadCsv } from "@/lib/csv-export";

import { SiteHeader } from "@/components/site/SiteHeader";
import { AdminNav } from "@/components/admin/AdminNav";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  acknowledgeRecoveryAlert,
  analyzeRecoveryCase,
  decideRecoveryAction,
  getRecoveryCase,
  getRecoveryDashboard,
  listRecoveryCases,
  setRecoveryCaseStatus,
} from "@/lib/recovery.functions";
import {
  listRecoveryIntents,
  sweepRecoveryOpportunities,
} from "@/lib/recovery-intent.functions";
import { CASE_STATUSES } from "@/lib/recovery/rules";

const INTENT_STAGES = [
  { key: "all", label: "Todas" },
  { key: "viewed", label: "Vio el tour" },
  { key: "interest", label: "Mostró interés" },
  { key: "checkout_started", label: "Inició pago" },
  { key: "checkout_error", label: "Error en el pago" },
  { key: "abandoned", label: "Abandonó" },
  { key: "converted", label: "Compró" },
] as const;

function IntentFunnel() {
  const qc = useQueryClient();
  const [stage, setStage] = useState<string>("checkout_started");
  const [msg, setMsg] = useState<string | null>(null);

  const intents = useQuery({
    queryKey: ["admin", "recovery", "intents", stage],
    queryFn: () => listRecoveryIntents({ data: { stage } }),
    staleTime: 15_000,
  });

  const sweep = useMutation({
    mutationFn: () => sweepRecoveryOpportunities({ data: { staleMinutes: 45 } }),
    onSuccess: (r) => {
      setMsg(
        `Barrido listo: ${r.fromIntents} oportunidades por pago iniciado, ${r.fromBookings} por reserva pendiente, ${r.skipped} ya registradas.`,
      );
      qc.invalidateQueries({ queryKey: ["admin", "recovery"] });
    },
    onError: (e: unknown) => setMsg(e instanceof Error ? e.message : String(e)),
  });

  const counts = intents.data?.counts ?? {};
  const rows = intents.data?.intents ?? [];

  return (
    <section className="space-y-3 rounded-lg border bg-card p-4">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-sm font-semibold">Embudo de intención (visitas, interés y pagos sin terminar)</h2>
        <Button
          size="sm"
          variant="outline"
          className="ml-auto"
          onClick={() => sweep.mutate()}
          disabled={sweep.isPending}
        >
          {sweep.isPending ? "Buscando…" : "Buscar oportunidades ahora"}
        </Button>
      </div>
      {msg && <p className="text-xs text-muted-foreground">{msg}</p>}
      <div className="flex flex-wrap gap-2">
        {INTENT_STAGES.map((s2) => (
          <button
            key={s2.key}
            onClick={() => setStage(s2.key)}
            className={`rounded-full border px-3 py-1 text-xs ${
              stage === s2.key ? "border-primary bg-primary/10 text-primary" : "text-muted-foreground"
            }`}
          >
            {s2.label}
            {counts[s2.key] != null && stage === "all" ? ` (${counts[s2.key]})` : ""}
          </button>
        ))}
      </div>
      {intents.isLoading ? (
        <Skeleton className="h-24 w-full" />
      ) : rows.length === 0 ? (
        <p className="text-xs text-muted-foreground">Sin registros en esta etapa.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="text-muted-foreground">
              <tr>
                <th className="py-2 pr-3">Última señal</th>
                <th className="py-2 pr-3">Tour</th>
                <th className="py-2 pr-3">Etapa</th>
                <th className="py-2 pr-3">Visitas</th>
                <th className="py-2 pr-3">Viajeros</th>
                <th className="py-2 pr-3">Importe</th>
                <th className="py-2 pr-3">Correo</th>
                <th className="py-2 pr-3">Detalle</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r: any) => (
                <tr key={r.id} className="border-t">
                  <td className="py-2 pr-3 whitespace-nowrap">
                    {r.last_seen_at ? new Date(r.last_seen_at).toLocaleString("es-MX") : "—"}
                  </td>
                  <td className="py-2 pr-3">{r.tour_title ?? r.tour_slug}</td>
                  <td className="py-2 pr-3">
                    <Badge variant="outline">{r.stage}</Badge>
                  </td>
                  <td className="py-2 pr-3">{r.views ?? 1}</td>
                  <td className="py-2 pr-3">{r.travelers ?? "—"}</td>
                  <td className="py-2 pr-3">
                    {r.amount ? money(Number(r.amount), (r.currency ?? "MXN").toUpperCase()) : "—"}
                  </td>
                  <td className="py-2 pr-3">{r.email ?? "—"}</td>
                  <td className="py-2 pr-3 text-muted-foreground">{r.last_error ?? ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

export const Route = createFileRoute("/_authenticated/admin/maya-revenue-recovery")({
  head: () => ({
    meta: [
      { title: "Maya Revenue Recovery — Admin" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: RevenueRecovery,
});

const money = (v: number, c = "MXN") =>
  `${Number(v ?? 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${c}`;

function RevenueRecovery() {
  const [status, setStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [product, setProduct] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [openCase, setOpenCase] = useState<string | null>(null);
  const qc = useQueryClient();

  const dash = useQuery({
    queryKey: ["admin", "recovery", "dashboard"],
    queryFn: () => getRecoveryDashboard(),
    staleTime: 20_000,
  });
  const cases = useQuery({
    queryKey: ["admin", "recovery", "cases", status, search],
    queryFn: () => listRecoveryCases({ data: { status, search } }),
    staleTime: 15_000,
  });

  const ackMut = useMutation({
    mutationFn: (vars: { alertId: string }) => acknowledgeRecoveryAlert({ data: vars }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "recovery", "dashboard"] }),
  });

  const s = dash.data?.summary;

  const allCases = (cases.data?.cases ?? []) as any[];
  const customers = (cases.data?.customers ?? {}) as Record<string, any>;

  const products = useMemo(
    () => [...new Set(allCases.map((c) => c.product_title).filter(Boolean))].sort() as string[],
    [allCases],
  );

  const visibleCases = useMemo(() => {
    const fromTs = from ? new Date(`${from}T00:00:00`).getTime() : null;
    const toTs = to ? new Date(`${to}T23:59:59`).getTime() : null;
    return allCases.filter((c) => {
      if (product !== "all" && (c.product_title ?? "") !== product) return false;
      const t = c.detected_at ? new Date(c.detected_at).getTime() : null;
      if (fromTs != null && (t == null || t < fromTs)) return false;
      if (toTs != null && (t == null || t > toTs)) return false;
      return true;
    });
  }, [allCases, product, from, to]);

  const exportCsv = () => {
    const columns = [
      { key: "caso", label: "Caso" },
      { key: "detectado", label: "Detectado" },
      { key: "disparador", label: "Disparador" },
      { key: "cliente", label: "Cliente" },
      { key: "correo", label: "Correo" },
      { key: "referencia", label: "Referencia externa" },
      { key: "producto", label: "Producto" },
      { key: "canal", label: "Canal" },
      { key: "fecha_actividad", label: "Fecha de actividad" },
      { key: "viajeros", label: "Viajeros" },
      { key: "estado", label: "Estado" },
      { key: "prioridad", label: "Prioridad" },
      { key: "motivo", label: "Motivo de pérdida" },
      { key: "accion", label: "Acción recomendada" },
      { key: "resultado", label: "Resultado" },
      { key: "importe", label: "Importe en riesgo" },
      { key: "recuperado", label: "Importe recuperado" },
      { key: "moneda", label: "Moneda" },
    ];
    const rows = visibleCases.map((c) => {
      const cust = c.booking_id ? customers[c.booking_id] : null;
      return {
        caso: c.case_code ?? "",
        detectado: c.detected_at ? new Date(c.detected_at).toLocaleString("es-MX") : "",
        disparador: c.trigger_type ?? "",
        cliente: cust?.name ?? "",
        correo: cust?.email ?? "",
        referencia: c.external_booking_ref ?? "",
        producto: c.product_title ?? "",
        canal: c.channel ?? "",
        fecha_actividad: c.activity_date ?? "",
        viajeros: c.travelers ?? "",
        estado: c.status ?? "",
        prioridad: c.priority ?? "",
        motivo: c.loss_reason ?? "",
        accion: c.recommended_action ?? "",
        resultado: c.resolution ?? "",
        importe: c.amount_at_risk ?? "",
        recuperado: c.recovered_amount ?? "",
        moneda: (c.currency ?? "MXN").toUpperCase(),
      };
    });
    const range = from || to ? `_${from || "inicio"}_a_${to || "hoy"}` : "";
    downloadCsv(`recovery_casos_${status}${range}`, columns, rows);
  };

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <div className="mx-auto max-w-7xl space-y-6 px-4 py-10 sm:px-6 lg:px-8">
        <div>
          <h1 className="font-display text-4xl font-semibold">Maya Revenue Recovery</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Supervisión de GetYourGuide/Bókun, detección de ventas perdidas y recuperación con
            autorización humana. Maya nunca bloquea una cancelación legítima ni modifica reservas por
            su cuenta.
          </p>
        </div>
        <AdminNav />

        {dash.isLoading ? (
          <Skeleton className="h-28 w-full" />
        ) : (
          <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-7">
            <Kpi label="💰 Ingresos recuperados" value={money(s?.recoveredAmount ?? 0)} />
            <Kpi label="📈 Tasa de recuperación" value={`${s?.recoveryRate ?? 0}%`} />
            <Kpi label="🔴 Ventas perdidas" value={String(s?.lostCount ?? 0)} sub={money(s?.lostAmount ?? 0)} />
            <Kpi label="🟠 Oportunidades" value={String(s?.openCount ?? 0)} />
            <Kpi label="🟢 Recuperadas" value={String(s?.recoveredCount ?? 0)} />
            <Kpi label="⚠️ Errores de sync" value={String(dash.data?.deadLetterCount ?? 0)} />
            <Kpi label="👤 Requieren humano" value={String(s?.humanCount ?? 0)} />
          </div>
        )}

        {(dash.data?.alerts?.length ?? 0) > 0 && (
          <div className="space-y-2 rounded-lg border border-destructive/40 bg-destructive/5 p-4">
            <h2 className="text-sm font-semibold text-destructive">Alertas abiertas</h2>
            {(dash.data?.alerts ?? []).map((a: any) => (
              <div key={a.id} className="flex flex-wrap items-center gap-2 text-sm">
                <Badge variant={a.severity === "critical" ? "destructive" : "outline"}>{a.alert_type}</Badge>
                <span className="flex-1">{a.message}</span>
                <span className="text-xs text-muted-foreground">×{a.occurrences}</span>
                <button
                  className="text-xs font-semibold text-primary hover:underline"
                  onClick={() => ackMut.mutate({ alertId: a.id })}
                >
                  Marcar visto
                </button>
              </div>
            ))}
          </div>
        )}

        <IntentFunnel />

        <div className="grid gap-3 sm:grid-cols-2">
          <Tally title="Principales causas de pérdida" rows={dash.data?.byLossReason ?? []} />
          <Tally title="Productos con más casos" rows={dash.data?.byProduct ?? []} />
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {["all", ...CASE_STATUSES].map((st) => (
            <button
              key={st}
              onClick={() => setStatus(st)}
              className={`rounded-full border px-3 py-1 text-xs ${
                status === st ? "border-primary bg-primary/10 text-primary" : "text-muted-foreground"
              }`}
            >
              {st}
            </button>
          ))}
          <Input
            className="ml-auto h-9 max-w-sm"
            placeholder="Buscar caso, producto, referencia…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="flex flex-wrap items-end gap-2 rounded-lg border bg-card p-3">
          <label className="text-xs text-muted-foreground">
            Producto
            <select
              className="mt-1 block h-9 rounded-md border bg-background px-2 text-sm text-foreground"
              value={product}
              onChange={(e) => setProduct(e.target.value)}
            >
              <option value="all">Todos</option>
              {products.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </label>
          <label className="text-xs text-muted-foreground">
            Desde (detección)
            <Input type="date" className="mt-1 h-9" value={from} onChange={(e) => setFrom(e.target.value)} />
          </label>
          <label className="text-xs text-muted-foreground">
            Hasta (detección)
            <Input type="date" className="mt-1 h-9" value={to} onChange={(e) => setTo(e.target.value)} />
          </label>
          {(product !== "all" || from || to) && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setProduct("all");
                setFrom("");
                setTo("");
              }}
            >
              Limpiar
            </Button>
          )}
          <div className="ml-auto flex items-center gap-3">
            <span className="text-xs text-muted-foreground">{visibleCases.length} casos</span>
            <Button size="sm" variant="outline" disabled={visibleCases.length === 0} onClick={exportCsv}>
              Exportar CSV
            </Button>
          </div>
        </div>

        {cases.isLoading ? (
          <Skeleton className="h-72 w-full" />
        ) : visibleCases.length === 0 ? (
          <p className="rounded-lg border bg-card p-8 text-center text-sm text-muted-foreground">
            Sin casos de recuperación registrados.
          </p>
        ) : (
          <div className="overflow-x-auto rounded-lg border bg-card">
            <table className="w-full min-w-[1100px] text-sm">
              <thead className="bg-muted/50 text-left text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="px-3 py-2">Caso</th>
                  <th className="px-3 py-2">Cliente</th>
                  <th className="px-3 py-2">Producto</th>
                  <th className="px-3 py-2">Canal</th>
                  <th className="px-3 py-2">Fecha</th>
                  <th className="px-3 py-2">Estado</th>
                  <th className="px-3 py-2">Motivo</th>
                  <th className="px-3 py-2">Acción Maya</th>
                  <th className="px-3 py-2">Resultado</th>
                  <th className="px-3 py-2">Importe</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {visibleCases.map((c: any) => {
                  const cust = c.booking_id ? customers[c.booking_id] : null;
                  return (
                    <tr key={c.id} className="border-t align-top hover:bg-muted/30">
                      <td className="px-3 py-2 font-mono text-xs">
                        {c.case_code}
                        <div className="text-[10px] text-muted-foreground">{c.trigger_type}</div>
                      </td>
                      <td className="px-3 py-2">
                        {cust ? (
                          <>
                            <div className="font-medium">{cust.name}</div>
                            <div className="text-xs text-muted-foreground">{cust.email}</div>
                          </>
                        ) : (
                          <span className="text-xs text-muted-foreground">
                            {c.external_booking_ref ?? "—"}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2">{c.product_title ?? "—"}</td>
                      <td className="px-3 py-2 text-xs">{c.channel ?? "—"}</td>
                      <td className="px-3 py-2 text-xs">{c.activity_date ?? "—"}</td>
                      <td className="px-3 py-2">
                        <CaseBadge status={c.status} />
                        <div className="text-[10px] text-muted-foreground">prio {c.priority}</div>
                      </td>
                      <td className="px-3 py-2 text-xs">{c.loss_reason ?? "—"}</td>
                      <td className="max-w-[220px] px-3 py-2 text-xs">{c.recommended_action ?? "—"}</td>
                      <td className="max-w-[180px] px-3 py-2 text-xs">{c.resolution ?? "—"}</td>
                      <td className="whitespace-nowrap px-3 py-2 text-xs">
                        {c.amount_at_risk != null ? money(Number(c.amount_at_risk), c.currency ?? "MXN") : "—"}
                        {c.recovered_amount != null && (
                          <div className="text-[10px] text-emerald-600">
                            +{money(Number(c.recovered_amount), c.currency ?? "MXN")}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <button
                          className="text-xs font-semibold text-primary hover:underline"
                          onClick={() => setOpenCase(openCase === c.id ? null : c.id)}
                        >
                          {openCase === c.id ? "Cerrar" : "Abrir"}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {openCase && <CaseDetail caseId={openCase} />}
      </div>
    </div>
  );
}

function Kpi({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="rounded-lg border bg-card p-3">
      <div className="text-[11px] text-muted-foreground">{label}</div>
      <div className="mt-1 text-xl font-semibold">{value}</div>
      {sub && <div className="text-[11px] text-muted-foreground">{sub}</div>}
    </div>
  );
}

function Tally({ title, rows }: { title: string; rows: Array<[string, number]> }) {
  return (
    <div className="rounded-lg border bg-card p-4">
      <h2 className="text-sm font-semibold">{title}</h2>
      {rows.length === 0 ? (
        <p className="mt-2 text-xs text-muted-foreground">Sin datos todavía.</p>
      ) : (
        <ul className="mt-2 space-y-1 text-sm">
          {rows.map(([k, n]) => (
            <li key={k} className="flex justify-between">
              <span className="text-muted-foreground">{k}</span>
              <span className="font-medium">{n}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function CaseBadge({ status }: { status: string }) {
  const variant: "default" | "secondary" | "destructive" | "outline" =
    status === "RECOVERED"
      ? "default"
      : status === "LOST"
        ? "destructive"
        : status === "REQUIRES_HUMAN"
          ? "destructive"
          : status === "RECOVERY_AVAILABLE" || status === "RECOVERY_IN_PROGRESS"
            ? "secondary"
            : "outline";
  return <Badge variant={variant}>{status}</Badge>;
}

function CaseDetail({ caseId }: { caseId: string }) {
  const qc = useQueryClient();
  const detail = useQuery({
    queryKey: ["admin", "recovery", "case", caseId],
    queryFn: () => getRecoveryCase({ data: { caseId } }),
  });
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["admin", "recovery"] });
  };
  const analyzeMut = useMutation({
    mutationFn: () => analyzeRecoveryCase({ data: { caseId } }),
    onSuccess: (res) => {
      invalidate();
      if (!res.ok) alert(`Maya no pudo analizar: ${res.error}`);
    },
  });
  const decideMut = useMutation({
    mutationFn: (vars: { actionId: string; decision: "approve" | "reject" }) =>
      decideRecoveryAction({ data: vars }),
    onSuccess: (res) => {
      invalidate();
      if (!res.ok) alert(res.error ?? "No se pudo aplicar la decisión.");
    },
  });
  const statusMut = useMutation({
    mutationFn: (vars: { status: string; resolution?: string; recoveredAmount?: number }) =>
      setRecoveryCaseStatus({ data: { caseId, ...vars } }),
    onSuccess: invalidate,
  });

  if (detail.isLoading) return <Skeleton className="h-64 w-full" />;
  const k = detail.data?.case as any;
  if (!k) return null;
  const maya = k.ai_analysis?.maya ?? null;
  const alts = k.ai_analysis?.alternatives ?? null;

  return (
    <div className="space-y-4 rounded-lg border bg-card p-5">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="font-display text-2xl font-semibold">{k.case_code}</h2>
        <CaseBadge status={k.status} />
        <Button size="sm" variant="outline" disabled={analyzeMut.isPending} onClick={() => analyzeMut.mutate()}>
          {analyzeMut.isPending ? "Analizando…" : "Analizar con Maya"}
        </Button>
      </div>

      {maya && (
        <div className="rounded-md border border-primary/30 bg-primary/5 p-3 text-sm">
          <div className="font-semibold">Análisis de Maya (informativo)</div>
          <p className="mt-1 text-muted-foreground">{maya.explanation}</p>
          {maya.recommended_action && (
            <p className="mt-1">
              <strong>Recomendación:</strong> {maya.recommended_action}
            </p>
          )}
        </div>
      )}

      {alts && (
        <div className="text-sm">
          <div className="font-semibold">Alternativas legítimas disponibles</div>
          <p className="text-xs text-muted-foreground">
            {(alts.dates ?? []).length > 0
              ? `Fechas: ${(alts.dates ?? []).map((d: any) => `${d.date}${d.time ? ` ${d.time}` : ""}`).join(", ")}`
              : "Sin fechas alternativas en inventario propio."}
          </p>
          {(alts.products ?? []).length > 0 && (
            <p className="text-xs text-muted-foreground">
              Experiencias: {(alts.products ?? []).map((p: any) => p.title).join(", ")}
            </p>
          )}
        </div>
      )}

      <div>
        <h3 className="text-sm font-semibold">Acciones y autorizaciones</h3>
        <ul className="mt-2 space-y-2">
          {(detail.data?.actions ?? []).map((a: any) => (
            <li key={a.id} className="flex flex-wrap items-center gap-2 rounded border p-2 text-sm">
              <Badge variant={a.authorization_level === 3 ? "destructive" : a.authorization_level === 2 ? "secondary" : "outline"}>
                Nivel {a.authorization_level}
              </Badge>
              <span className="font-medium">{a.action_type}</span>
              <span className="flex-1 text-xs text-muted-foreground">{a.rationale}</span>
              <Badge variant="outline">{a.status}</Badge>
              {a.status === "AWAITING_APPROVAL" && (
                <>
                  <button
                    className="text-xs font-semibold text-primary hover:underline"
                    onClick={() => decideMut.mutate({ actionId: a.id, decision: "approve" })}
                  >
                    Aprobar
                  </button>
                  <button
                    className="text-xs text-destructive hover:underline"
                    onClick={() => decideMut.mutate({ actionId: a.id, decision: "reject" })}
                  >
                    Rechazar
                  </button>
                </>
              )}
              {a.result?.note && (
                <span className="w-full text-[11px] text-muted-foreground">{a.result.note}</span>
              )}
            </li>
          ))}
        </ul>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-muted-foreground">Cerrar caso como:</span>
        <Button
          size="sm"
          onClick={() => {
            const amount = Number(prompt("Importe recuperado:", String(k.amount_at_risk ?? 0)) ?? 0);
            statusMut.mutate({
              status: "RECOVERED",
              resolution: prompt("Resultado (opcional):") ?? undefined,
              recoveredAmount: Number.isFinite(amount) ? amount : 0,
            });
          }}
        >
          Recuperado
        </Button>
        <Button
          size="sm"
          variant="destructive"
          onClick={() =>
            statusMut.mutate({ status: "LOST", resolution: prompt("Motivo de la pérdida:") ?? undefined })
          }
        >
          Perdido
        </Button>
        <Button size="sm" variant="outline" onClick={() => statusMut.mutate({ status: "REQUIRES_HUMAN" })}>
          Requiere humano
        </Button>
        <Button size="sm" variant="outline" onClick={() => statusMut.mutate({ status: "CLOSED" })}>
          Cerrar
        </Button>
      </div>
    </div>
  );
}
